import { Injectable, Inject, PLATFORM_ID, Optional, DOCUMENT } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import {
  DEFAULT_LOCALE,
  isValidLocale,
  LocaleCode,
} from 'src/app/Core/locale/locale.constants';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  constructor(
    private title: Title,
    private meta: Meta,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject('REQUEST_URL') private requestUrl: string
  ) {}

  setSeo(title: string, description: string, imageUrl: string = '') {
    // Set the title
    this.title.setTitle(title);

    // Create full image URL
    const fullImageUrl = imageUrl
      ? imageUrl.startsWith('http')
        ? imageUrl
        : `${environment.url_front}${imageUrl}`
      : `${environment.url_front}/assets/images/logo-header.png`;

    // Get the current URL (works in both browser and server)
    const currentUrl = this.getPageUrl();
    const canonicalUrl = this.normalizeCanonicalUrl(currentUrl);

    this.meta.updateTag({ name: 'description', content: description });

    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: fullImageUrl });
    this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
    this.meta.updateTag({ property: 'og:type', content: 'website' });

    // Twitter meta tags
    this.meta.updateTag({ property: 'twitter:title', content: title });
    this.meta.updateTag({
      property: 'twitter:description',
      content: description,
    });
    this.meta.updateTag({
      property: 'twitter:card',
      content: 'summary_large_image',
    });
    this.meta.updateTag({ property: 'twitter:image', content: fullImageUrl });

    // Set canonical URL
    this.setCanonicalLink(canonicalUrl);

    const { locale, pathWithoutLocale } = this.getPathContext(canonicalUrl);
    this.setHreflangTags(pathWithoutLocale, locale);
  }

  private clearMetaTags(): void {
    // Remove existing meta tags to avoid duplicates
    this.meta.removeTag("name='description'");
    this.meta.removeTag("property='og:title'");
    this.meta.removeTag("property='og:description'");
    this.meta.removeTag("property='og:image'");
    this.meta.removeTag("property='og:url'");
    this.meta.removeTag("property='og:type'");
    this.meta.removeTag("name='twitter:title'");
    this.meta.removeTag("name='twitter:description'");
    this.meta.removeTag("name='twitter:card'");
    this.meta.removeTag("name='twitter:image'");
  }

  private getPageUrl(): string {
    // In browser, use the location
    if (isPlatformBrowser(this.platformId)) {
      return this.document.location.href;
    }

    // In server, use the injected REQUEST_URL
    if (this.requestUrl) {
      return this.requestUrl;
    }

    // Fallback if REQUEST_URL is not available
    return environment.url_front;
  }

  private setCanonicalLink(url: string): void {
    // Remove existing canonical links
    const existingCanonicalLink = this.document.querySelector(
      'link[rel="canonical"]'
    );
    if (existingCanonicalLink) {
      existingCanonicalLink.remove();
    }

    // Add new canonical link
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    this.document.head.appendChild(link);
  }

  setHreflangTags(currentPath: string, _currentLocale: LocaleCode): void {
    this.removeHreflangTags();

    const normalizedPath = this.normalizePath(currentPath);
    const baseUrl = this.getBaseUrl();

    this.addHreflangTag('en', `${baseUrl}/en${normalizedPath}`);
    this.addHreflangTag('ar', `${baseUrl}/ar${normalizedPath}`);
    this.addHreflangTag('x-default', `${baseUrl}/`);
  }

  private removeHreflangTags(): void {
    const tags = this.document.querySelectorAll(
      'link[rel="alternate"][hreflang]'
    );
    tags.forEach((tag) => tag.remove());
  }

  private addHreflangTag(hreflang: string, href: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', href);
    this.document.head.appendChild(link);
  }

  private normalizePath(path: string): string {
    if (!path || path === '/') {
      return '/';
    }

    const normalized = path.startsWith('/') ? path : `/${path}`;
    return normalized.replace(/\/+$/, '') || '/';
  }

  private getBaseUrl(): string {
    return environment.url_front.replace(/\/+$/, '');
  }

  private normalizeCanonicalUrl(url: string): string {
    const parsed = new URL(url, environment.url_front);
    parsed.pathname = this.normalizeCanonicalPath(parsed.pathname);
    return parsed.toString();
  }

  private normalizeCanonicalPath(path: string): string {
    const normalizedWithLeadingSlash = path.startsWith('/') ? path : `/${path}`;
    const collapsed = normalizedWithLeadingSlash.replace(/\/{2,}/g, '/');
    const withoutTrailingSlash =
      collapsed === '/' ? '/' : collapsed.replace(/\/+$/, '');

    const segments = withoutTrailingSlash.split('/').filter(Boolean);
    const firstSegment = segments[0]?.toLowerCase() ?? '';

    if (isValidLocale(firstSegment) && segments.length === 1) {
      return `/${firstSegment}/`;
    }

    return withoutTrailingSlash || '/';
  }

  private getPathContext(currentUrl: string): {
    locale: LocaleCode;
    pathWithoutLocale: string;
  } {
    const parsed = new URL(currentUrl, environment.url_front);
    const segments = parsed.pathname.split('/').filter(Boolean);

    const firstSegment = segments[0]?.toLowerCase() ?? '';
    const locale = isValidLocale(firstSegment) ? firstSegment : DEFAULT_LOCALE;
    const rest = isValidLocale(firstSegment) ? segments.slice(1) : segments;

    return {
      locale,
      pathWithoutLocale: rest.length ? `/${rest.join('/')}` : '/',
    };
  }
}
